<main class="portal-page">
  <div class="loading-view" *ngIf="loading" role="status" aria-live="polite">
    <span class="loading-spinner" aria-hidden="true"></span>
    <p>Checking your session…</p>
  </div>

  <section class="login-shell" *ngIf="!loading && !session" aria-labelledby="sign-in-title">
    <div class="login-frame">
      <img class="kasha-logo" src="assets/img/kasha_logo.png" alt="Kasha">
      <div class="login-card">
        <h1 id="sign-in-title">ONE Kasha Platform</h1>
        <p class="login-intro">Sign in with your account to access ONE Kasha Platform</p>
        <div id="google-signin" class="google-signin" aria-label="Sign in with Google"></div>
        <div class="login-divider"><span>or</span></div>
        <p class="auth-message auth-message--error" *ngIf="errorMessage" role="alert">{{ errorMessage }}</p>
        <button class="email-signin-button" type="button" (click)="openEmailLogin()">
          Sign in with Email
        </button>
      </div>
    </div>

    <p class="login-security">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l7 3v5c0 4.6-2.9 8.1-7 10-4.1-1.9-7-5.4-7-10V6l7-3z"></path><path d="M9.3 12l1.8 1.8 3.8-4"></path></svg>
      Secure central access for authorized ONE Kasha Platform users
    </p>

    <div
      *ngIf="emailModalOpen"
      class="modal-backdrop modal-backdrop--open"
      (click)="closeEmailLogin()">
      <div class="email-modal" role="dialog" aria-modal="true" aria-labelledby="email-modal-title" (click)="$event.stopPropagation()">
        <button class="modal-close" type="button" (click)="closeEmailLogin()" aria-label="Close email sign in">&times;</button>
        <h2 id="email-modal-title">Sign in</h2>
        <form [formGroup]="loginForm" (ngSubmit)="signIn()" novalidate>
          <label class="sr-only" for="email">Email address</label>
          <input
            id="email"
            type="email"
            formControlName="email"
            autocomplete="email"
            placeholder="Email"
            [attr.aria-invalid]="loginForm.controls.email.invalid && loginForm.controls.email.touched">

          <label class="sr-only" for="password">Password</label>
          <div class="password-field">
            <input
              id="password"
              [type]="passwordVisible ? 'text' : 'password'"
              formControlName="password"
              autocomplete="current-password"
              placeholder="Password"
              [attr.aria-invalid]="loginForm.controls.password.invalid && loginForm.controls.password.touched">
            <button
              type="button"
              (click)="passwordVisible = !passwordVisible"
              [attr.aria-label]="passwordVisible ? 'Hide password' : 'Show password'">
              {{ passwordVisible ? 'Hide' : 'Show' }}
            </button>
          </div>

          <p class="auth-message auth-message--error" *ngIf="errorMessage" role="alert">{{ errorMessage }}</p>
          <button class="sign-in-button" type="submit" [disabled]="submitting">
            <span>{{ submitting ? 'Signing in…' : 'Sign In' }}</span>
          </button>
        </form>
      </div>
    </div>
  </section>

  <div class="portal-shell" *ngIf="!loading && session">
    <div class="session-bar">
      <div class="session-user">
        <span class="user-avatar" aria-hidden="true">{{ session.email.charAt(0).toUpperCase() }}</span>
        <span>Signed in as <strong>{{ session.email }}</strong></span>
      </div>
      <button type="button" (click)="logout()">Sign out</button>
    </div>

    <header class="portal-header">
      <img class="kasha-logo" src="assets/img/kasha_logo.png" alt="Kasha">
      <h1>ONE Kasha Platform</h1>
      <p>{{ grantedCountryLabel ? 'Your access: ' + grantedCountryLabel : 'Select an authorized service to continue' }}</p>
    </header>

    <section
      *ngFor="let area of visibleAccessAreas; trackBy: trackArea"
      class="access-area"
      [ngClass]="'access-area--' + area.id"
      [attr.aria-labelledby]="area.id + '-title'">
      <div class="area-header">
        <div class="area-heading">
          <span class="icon-badge icon-badge--large" aria-hidden="true">
            <ng-container *ngTemplateOutlet="icon; context: {$implicit: area.icon}"></ng-container>
          </span>
          <div>
            <h2 [id]="area.id + '-title'">{{ areaTitle(area) }}</h2>
            <p>{{ areaDescription(area) }}</p>
          </div>
        </div>
        <span class="area-access-note">Choose a service below</span>
      </div>

      <div class="service-grid">
        <button
          class="service-card"
          type="button"
          *ngFor="let service of visibleServices(area); trackBy: trackService"
          (click)="accessService(service)"
          [disabled]="openingService === service.title"
          [class.service-card--placeholder]="service.placeholder"
          [attr.aria-label]="(service.placeholder ? 'Coming soon: ' : 'Open ') + service.title">
          <span class="icon-badge" aria-hidden="true">
            <ng-container *ngTemplateOutlet="icon; context: {$implicit: service.icon}"></ng-container>
          </span>
          <div class="service-copy">
            <h3>{{ service.title }}</h3>
            <p>{{ service.description }}</p>
          </div>
          <span class="service-launch" aria-hidden="true">
            {{ service.placeholder ? 'Soon' : (openingService === service.title ? '…' : '→') }}
          </span>
        </button>
      </div>
    </section>

    <section class="no-access" *ngIf="visibleAccessAreas.length === 0" role="status">
      <span class="icon-badge icon-badge--large" aria-hidden="true">
        <ng-container *ngTemplateOutlet="icon; context: {$implicit: 'person'}"></ng-container>
      </span>
      <h2>No ONE Kasha Platform access assigned</h2>
      <p>Your login is valid, but your profile is not assigned to Rwanda or Nigeria organisations or roles.</p>
      <p>Contact your CMS administrator to request Rwanda (<code>rw_pds_*</code>), Nigeria (<code>ng_pds_*</code>), finance (<code>rw_pds_finance</code> / <code>ng_pds_finance</code>), SC Johnson (<code>sc_johnson</code>), partner, CXO (<code>cxo-business-user</code>), or production support (<code>admin-eng</code> / <code>administrator</code> / <code>super-admin</code>) access.</p>
    </section>

    <p class="auth-message auth-message--notice" *ngIf="errorMessage" role="status">{{ errorMessage }}</p>
  </div>
</main>

<ng-template #icon let-name>
  <svg *ngIf="name === 'globe'" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"></circle><path d="M3 12h18M12 3c3 3 4 6 4 9s-1 6-4 9M12 3c-3 3-4 6-4 9s1 6 4 9"></path></svg>
  <svg *ngIf="name === 'person'" viewBox="0 0 24 24"><circle cx="12" cy="7" r="3.5"></circle><path d="M5.5 21v-2.2a6.5 6.5 0 0113 0V21"></path></svg>
  <svg *ngIf="name === 'truck'" viewBox="0 0 24 24"><path d="M3 5h11v11H3zM14 9h4l3 4v3h-7z"></path><circle cx="7" cy="18" r="2"></circle><circle cx="18" cy="18" r="2"></circle></svg>
  <svg *ngIf="name === 'chart'" viewBox="0 0 24 24"><path d="M5 20v-5M10 20V9M15 20V5M20 20V2"></path></svg>
  <svg *ngIf="name === 'monitor'" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="13" rx="1.5"></rect><path d="M8 21h8M12 17v4M6 13l4-4 3 2 5-5"></path></svg>
  <svg *ngIf="name === 'snowflake'" viewBox="0 0 24 24"><path d="M12 3v18M5 7l14 10M19 7L5 17M4 12h16"></path></svg>
  <svg *ngIf="name === 'trend'" viewBox="0 0 24 24"><path d="M3 20h18M5 17l5-6 4 3 6-9M15 5h5v5"></path></svg>
  <svg *ngIf="name === 'brain'" viewBox="0 0 24 24"><path d="M9 4a3 3 0 00-5 2 3 3 0 00-1 5 3 3 0 002 5 3 3 0 004 3M15 4a3 3 0 015 2 3 3 0 011 5 3 3 0 01-2 5 3 3 0 01-4 3M9 4v16M15 4v16"></path></svg>
</ng-template>
